import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

// Components
import Form from '../../Form';
import InputField from '../../InputField';
import PasswordField from '../../PasswordField';
import PhoneField from '../../PhoneField';
import Button from '../../Button';
import SuccessMessage from '../../SuccessMessage';
import CheckboxField from '../../CheckboxField';
import AppLink from '../../AppLink';

// Router
import { getRoute } from '../../../entry/routes';

// State
import { signUp } from '../../../state/modules/user/actions';
import userApi from '../../../state/modules/user/api';

// Utils
import {
  isEmail,
  numberByCode,
  required,
  requiredNumber,
  strongPassword,
} from '../../../utils/validators';

// Styles
import styles from './SignUpForm.styl';
import config from '../../../config';
import CheckboxFieldRed from '../../CheckboxFieldRed';
import ToggleArrow from '../../ToggleArrow';
import Collapse from '../../Collapse';

const cx = classNames.bind(styles);

export default function SignUpForm(props) {
  const { className, onCancel, onSuccessCallback } = props;

  const intl = useIntl();
  const dispatch = useDispatch();
  const {
    signUp: { error, isSigningUp, isSignUp },
  } = useSelector(state => state.user);

  const [isFirstStep, setIsFirstStep] = useState(true);
  const [isCheckbox3Enabled, setIsCheckbox3Enabled] = useState(false);
  const [isCheckbox4Enabled, setIsCheckbox4Enabled] = useState(false);
  const [isCheckbox5Enabled, setIsCheckbox5Enabled] = useState(false);

  const isButtonDisabled = config.legal === 'pl' ? isFirstStep || !isCheckbox5Enabled : isFirstStep;
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => {
    setIsOpen(prev => !prev);
  };
  const { register, handleSubmit, errors, control, setError, trigger, getValues } = useForm({
    mode: 'onBlur',
    defaultValues: { username: '', phone: '', email: '', password: '', password_repeat: '' },
  });

  const onFirstStepSubmit = async () => {
    const result = await trigger(['username', 'phone', 'email']);

    if (!result) {
      return false;
    }

    let hasError = false;
    const { phone, email } = getValues(['phone', 'email']);

    await userApi
      .hasPhone(phone)
      .then(({ status }) => {
        if (status) {
          hasError = true;
          setError('phone', {
            type: 'manual',
            message: intl.formatMessage({ id: 'signUp.existPhone' }),
          });
        }
      })
      .catch(({ message }) => {
        hasError = true;
        setError('phone', {
          type: 'manual',
          message,
        });
      });

    await userApi
      .hasMail(email)
      .then(({ status }) => {
        if (status) {
          hasError = true;
          setError('email', {
            type: 'manual',
            message: intl.formatMessage({ id: 'signUp.existEmail' }),
          });
        }
      })
      .catch(({ message }) => {
        hasError = true;
        setError('email', {
          type: 'manual',
          message,
        });
      });

    return hasError ? false : setIsFirstStep(false);
  };

  const onSubmit = handleSubmit(async formValues => {
    try {
      await dispatch(
        signUp(
          {
            ...formValues,
            accepted_loyalty_program: true,
            sms_state: isCheckbox3Enabled,
            subscription_state: isCheckbox4Enabled,
          },
          onSuccessCallback,
        ),
      );
    } catch (e) {}
  });

  if (isSignUp) {
    return (
      <SuccessMessage
        className={cx('SignUpForm__success')}
        title={intl.formatMessage({ id: 'signUp.successMessage.title' })}
        text={intl.formatMessage({ id: 'signUp.successMessage.text' })}
      />
    );
  }

  const marketingConsent = isCheckbox3Enabled && isCheckbox4Enabled;
  const toggleMarketingConsent = () => {
    const next = !marketingConsent;
    setIsCheckbox3Enabled(next);
    setIsCheckbox4Enabled(next);
    // dispatch(setCheckoutUserData({ sms_state: next, subscription_state: next }));
  };
  // useEffect(() => {
  //   return () => {
  //     dispatch(resetSignUp());
  //   };
  // }, []);
  return (
    <form className={cx('SignUpForm', className)} onSubmit={onSubmit}>
      <div className={cx('SignUpForm__step', { SignUpForm__step_visible: isFirstStep })}>
        <div className={cx('SignUpForm__field')}>
          <InputField
            name="username"
            label={intl.formatMessage({ id: 'field.username.label' })}
            register={register({ validate: { required } })}
          />
          {errors.username && (
            <div className={cx('SignUpForm__error')}>{errors.username.message}</div>
          )}
        </div>
        <div className={cx('SignUpForm__field')}>
          <Controller
            name="phone"
            control={control}
            rules={{ validate: { requiredNumber, numberByCode } }}
            render={({ onChange, value, name, onBlur }) => (
              <PhoneField
                name={name}
                value={value}
                label={intl.formatMessage({ id: 'field.phone.label' })}
                onChange={onChange}
                onBlur={onBlur}
              />
            )}
          />
          {errors.phone && <div className={cx('SignUpForm__error')}>{errors.phone.message}</div>}
        </div>
        <div className={cx('SignUpForm__field')}>
          <InputField
            name="email"
            label="Email"
            register={register({ validate: { required, isEmail } })}
            onKeyDown={e => e.key === 'Enter' && onFirstStepSubmit()}
          />
          {errors.email && <div className={cx('SignUpForm__error')}>{errors.email.message}</div>}
        </div>
      </div>
      <div className={cx('SignUpForm__step', { SignUpForm__step_visible: !isFirstStep })}>
        <div className={cx('SignUpForm__field')}>
          <PasswordField
            name="password"
            label={intl.formatMessage({ id: 'field.password.label' })}
            register={register({ validate: { required, strongPassword } })}
          />
          {errors.password && (
            <div className={cx('SignUpForm__error')}>{errors.password.message}</div>
          )}
        </div>
        <div className={cx('SignUpForm__field')}>
          <PasswordField
            name="password_repeat"
            label={intl.formatMessage({ id: 'field.repeatPassword.label' })}
            register={register({
              validate: {
                required,
                passwordIsEqual: value =>
                  getValues('password') === value
                    ? undefined
                    : intl.formatMessage({ id: 'validators.passwordIsEqual' }),
              },
            })}
          />
          {errors.password_repeat && (
            <div className={cx('SignUpForm__error')}>{errors.password_repeat.message}</div>
          )}
        </div>
      </div>
      {config.legal === 'pl' && !isFirstStep && (
        <>
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '24px',
              }}
            >
              <CheckboxFieldRed
                className={cx('CheckoutFooter__privacy-title')}
                value={isCheckbox5Enabled}
                label={intl.formatMessage(
                  { id: 'privacy.checkbox5.title' },
                  {
                    link1: (
                      <AppLink to={getRoute('user-agreement')}>
                        {intl.formatMessage({ id: 'checkout.agreePrivacyPolicyLink' })}
                      </AppLink>
                    ),
                    link2: (
                      <AppLink to={getRoute('personal-data-processing-policy')}>
                        {intl.formatMessage({ id: 'checkout.agreePrivacyPolicyLink1' })}
                      </AppLink>
                    ),
                  },
                )}
                onChange={() => setIsCheckbox5Enabled(prev => !prev)}
              />
            </div>
            {!isCheckbox5Enabled && (
              <div className={cx('CheckoutFooter__mandatory')}>
                {intl.formatMessage({ id: 'privacy.checkbox.title.description' })}
              </div>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <CheckboxField
              className={cx('CheckoutFooter__privacy-title')}
              value={marketingConsent}
              name="marketing_consent"
              label={intl.formatMessage({ id: 'privacy.title' })}
              onChange={toggleMarketingConsent}
            />
            <button
              className={cx('CheckoutFooter__button')}
              onClick={toggle}
              onKeyDown={e => e.key === 'Enter' && toggle()}
              type="button"
            >
              <ToggleArrow className={cx('CommentField__arrow')} isOpen={isOpen} />
            </button>
          </div>
          <div className={cx('CheckoutFooter__privacy-description')}>
            {intl.formatMessage({ id: 'privacy.description' })}
          </div>
          <Collapse isOpen={isOpen}>
            <div className={cx('CheckoutFooter__stateContainer')}>
              <CheckboxField
                className={cx('CheckoutFooter__privacy-title')}
                value={isCheckbox3Enabled}
                name="sms_state"
                label={intl.formatMessage({ id: 'privacy.checkbox3.title' })}
                onChange={() => setIsCheckbox3Enabled(prev => !prev)}
              />
              <div className={cx('CheckoutFooter__privacy-description')}>
                {intl.formatMessage({ id: 'privacy.checkbox3.description' })}
              </div>
              <CheckboxField
                className={cx('CheckoutFooter__privacy-title')}
                value={isCheckbox4Enabled}
                name="subscription_state"
                label={intl.formatMessage({ id: 'privacy.checkbox4.title' })}
                onChange={() => setIsCheckbox4Enabled(prev => !prev)}
              />
              <div className={cx('CheckoutFooter__privacy-description')}>
                {intl.formatMessage({ id: 'privacy.checkbox4.description' })}
              </div>
            </div>
          </Collapse>
          <div className={cx('CheckoutFooter__privacy-description')}>
            {intl.formatMessage(
              { id: 'privacy.checkbox5.description' },
              {
                link1: (
                  <AppLink to={getRoute('user-agreement')}>
                    {intl.formatMessage({ id: 'checkout.agreePrivacyPolicyLink' })}
                  </AppLink>
                ),
                link2: (
                  <AppLink to={getRoute('personal-data-processing-policy')}>
                    {intl.formatMessage({ id: 'checkout.agreePrivacyPolicyLink1' })}
                  </AppLink>
                ),
              },
            )}
          </div>
        </>
      )}
      <div className={cx('SignUpForm__actions')}>
        <Button
          className={cx('SignUpForm__button', 'SignUpForm__button_left')}
          onClick={isFirstStep ? onCancel : () => setIsFirstStep(true)}
          link
        >
          {intl.formatMessage({ id: isFirstStep ? 'button.cancel' : 'button.back' })}
        </Button>
        {isFirstStep ? (
          <Button className={cx('SignUpForm__button')} onClick={onFirstStepSubmit} type="button">
            {intl.formatMessage({ id: 'button.continue' })}
          </Button>
        ) : (
          <Button
            className={cx('SignUpForm__button')}
            type="submit"
            isLoading={isSigningUp}
            disabled={isButtonDisabled}
          >
            {intl.formatMessage({ id: 'button.continue' })}
          </Button>
        )}
      </div>
      <Form.Error className={cx('SignUpForm__errors')} error={error} />
    </form>
  );
}

SignUpForm.defaultProps = {
  className: '',
  onSuccessCallback: undefined,
};

SignUpForm.propTypes = {
  className: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSuccessCallback: PropTypes.func,
};
